import React from 'react';
import { Tooltip, ToggleButton, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';
import { CTFModuleTypes, PDFTronTools } from 'types';
import { getSelectedTool, getIsStepAndRepeat } from 'store/inspection/inspectionSelectors';
import selectTool from 'utils/selectTool/selectTool';
import { MarkupIcon } from 'components/icons';
import { getStyleVariables } from 'styles/vars';
import { getSelectedModule } from 'store';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVars = getStyleVariables(theme);
  return {
    toggleButton: {
      borderRadius: '50%',
      width: '24px',
      height: '24px',
      padding: 0,
      margin: theme.spacing(0.25),
      color: 'rgba(255,255,255,.7)',
      border: 'none',
      '&:hover': {
        color: 'rgba(255,255,255)',
        backgroundColor: 'rgba(255,255,255,.04)',
      },
      '&.Mui-selected': {
        color: 'rgba(255,255,255)',
        backgroundColor: styleVars.colors.red,
      },
      '&.Mui-selected:hover': {
        backgroundColor: styleVars.colors.red,
      },
      '&.Mui-disabled': {
        color: 'rgba(255,255,255,.37) !important',
      },
    },
    icon: {
      width: theme.spacing(2),
      height: theme.spacing(2),
      cursor: 'pointer',
    },
    disabled: {
      color: 'rgba(255,255,255,.37) !important',
    },
  };
});

interface MarkupButtonProps {
  disabled?: boolean;
}

const MarkupToggleButton: React.FC<MarkupButtonProps> = ({ disabled = false }) => {
  const { classes } = useStyles();

  const selectedTool = useSelector(getSelectedTool);

  const isMarkupToolSelected = selectedTool === PDFTronTools.MARKUP;
  const isGraphicsModuleSelected = useSelector(getSelectedModule) === CTFModuleTypes.graphics;
  const isStepAndRepeat = useSelector(getIsStepAndRepeat);
  const isMarkupDisabled = isGraphicsModuleSelected && isStepAndRepeat;

  const isDisabled = disabled || isMarkupDisabled;

  const handleToggle = () => {
    if (isMarkupToolSelected) {
      selectTool(PDFTronTools.PAN);
    } else {
      selectTool(PDFTronTools.MARKUP);
    }
  };

  const tooltipTitle = isMarkupDisabled ? 'Not supported for repeats' : 'Add Markup';

  return (
    <Tooltip title={tooltipTitle} placement="bottom">
      <span>
        <ToggleButton
          value="markupButton"
          selected={isMarkupToolSelected}
          className={classes.toggleButton}
          onClick={handleToggle}
          disabled={isDisabled}
          classes={{ disabled: classes.disabled }}
          disableRipple
          data-testid="markup-button"
        >
          <MarkupIcon className={classes.icon} />
        </ToggleButton>
      </span>
    </Tooltip>
  );
};

export default MarkupToggleButton;
